import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useAxios from 'axios-hooks';
import api from '@lib/api';
import { Modal, Form, Input, Select, Button, message, Upload, Tooltip } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { Auth } from 'aws-amplify';
import { ReactComponent as IconLogout } from '@svg/icon-logout.svg';
// [#30]
// import { ReactComponent as IconThemeLight } from '@svg/icon-theme-light.svg';
// import { ReactComponent as IconThemeDark } from '@svg/icon-theme-dark.svg';
// import { ReactComponent as IconThemeDynamic } from '@svg/icon-theme-dynamic.svg';
import { ReactComponent as IconEdit } from '@svg/icon-edit.svg';
import { ReactComponent as IconDone } from '@svg/common/icon-done-white.svg';
import { ReactComponent as IconUsername } from '@svg/icon-user-name.svg';
import { ReactComponent as IconSetting } from '@svg/icon-user-setting.svg';
import { ReactComponent as IconLanguage } from '@svg/icon-user-language.svg';

import { useUIStore } from '@stores/ui';
import { scMixinScreenReaderOnly } from '@utils/emotion/mixins';
import { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface';
import { UploadPresignedUserAvatar } from '~/interfaces/api.interface';
import axios from 'axios';
import { IChannelData } from '~/interfaces/user.interface';
import { getBase64FromImage, getImageDimension } from '~/utils';
import { useAuthStore } from '@stores/auth';

const { Option } = Select;

const UserModal: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { userData } = useAuthStore();
  const { isUserModalVisible, closeUserModal } = useUIStore();
  const [{ data: channelData, loading: channelLoading, error: channelError }] =
    useAxios<IChannelData>(`/channel/${userData?.mainChannel}`, { useCache: false });
  const form = useRef<FormInstance | null>(null);
  const [onRequesting, setRequesting] = useState(false);
  const [isFormInvalid, setFormInvalid] = useState(false);
  const [isFocusedUserName, setFocusedUserName] = useState(false);
  const [isFocusedChannelName, setFocusedChannelName] = useState(false);
  const userPictureRef = useRef<HTMLImageElement | null>(null);
  const userPictureFileStore = useRef<File | null>(null);
  const defaultLanguage = i18n.languages[0];

  useEffect(() => {
    if (channelData && !channelLoading) {
      if (!form.current) return;

      form.current?.setFieldsValue({ channel_name: channelData.name });
    }

    if (channelError) {
      message.error(t('common.api.error'));
    }
  }, [t, channelLoading, channelData, channelError]);

  const onFieldsChange = () => {
    setFormInvalid(form.current!.getFieldsError().some((field) => field.errors.length));
  };

  const setAvatarPreview = useCallback(
    async (options: RcCustomRequestOptions) => {
      const { file } = options;

      if (!(file instanceof File)) return;

      if (file.size > 3 * (1024 * 1000)) {
        // Limit Up to 3MB
        return Modal.error({
          title: t('usermodal.info.avatar.error.size.title'),
          content: t('usermodal.info.avatar.error.size.description'),
          okText: t('a11y.ok'),
        });
      }

      try {
        const imageDimension = await getImageDimension(file);

        if (imageDimension.width > 1920 || imageDimension.height > 1920) {
          throw new Error('Too large dimension');
        }

        const imageBase64 = await getBase64FromImage(file);

        userPictureFileStore.current = file;
        userPictureRef.current!.src = imageBase64;
      } catch (e) {
        if (e instanceof Error) {
          switch (e.message) {
            case 'Too large dimension':
              Modal.error({
                title: t('usermodal.info.avatar.error.dimension.title'),
                content: t('usermodal.info.avatar.error.dimension.description'),
                okText: t('a11y.ok'),
              });
              break;
            default:
              Modal.error({
                title: t('video.common.warn.title'),
                content: t('error.subtitle'),
                okText: t('a11y.ok'),
              });
          }
        } else {
          Modal.error({
            title: t('video.common.warn.title'),
            content: t('error.subtitle'),
            okText: t('a11y.ok'),
          });
        }
      }
    },
    [t],
  );

  const uploadAvatar = useCallback(
    async (file: File): Promise<UploadPresignedUserAvatar> => {
      const res = await api
        .post<UploadPresignedUserAvatar>(`/user/${userData?.userId}/avatar/url`, {
          filename: file.name,
        })
        .catch((error) => {
          throw new Error(error);
        });

      const { url } = res.data;

      await axios.put(url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      return res.data;
    },
    [userData],
  );

  const changeName = useCallback(
    (name: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        api
          .patch(`/user/${userData?.userId}`, { name })
          .then(() => resolve())
          .catch((e) => reject(e));
      });
    },
    [userData],
  );

  const changeChannelName = useCallback(
    (name: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        api
          .patch(`/channel/${userData?.mainChannel}`, { name })
          .then(() => resolve())
          .catch((e) => reject(e));
      });
    },
    [userData],
  );

  const changeLanguage = useCallback(
    (value: string) => {
      dayjs.locale(value);
      i18n.changeLanguage(value);
    },
    [i18n],
  );

  const submit = useCallback(async () => {
    if (!form.current || isFormInvalid || channelLoading || channelError || !channelData) return;

    setRequesting(true);

    const fields = form.current.getFieldsValue();
    const reqs: Promise<any>[] = [];

    if (userData?.name !== fields.user_name) {
      reqs.push(changeName(fields.user_name));
    }

    if (channelData.name !== fields.channel_name) {
      reqs.push(changeChannelName(fields.channel_name));
    }

    if (userPictureFileStore.current) {
      reqs.push(uploadAvatar(userPictureFileStore.current));
    }

    if (reqs.length) {
      Promise.all(reqs)
        .then((values) => {
          const avatarRes = values.filter((v) => v);

          if (avatarRes.length && userData) {
            userData.avatarUrl = avatarRes[0]!.href;
          }

          if (userData) userData.name = fields.user_name;

          if (defaultLanguage !== fields.language) {
            window.setTimeout(() => {
              changeLanguage(fields.language);
              message.success(t('usermodal.info.message.success'), 4);
            }, 0);
          }

          closeUserModal();
        })
        .catch(() => {
          Modal.error({
            title: t('error.api.common.submit.server.title'),
            content: t('error.api.common.submit.server.content'),
            okText: t('a11y.ok'),
          });
          setRequesting(false);
        });
    } else {
      if (defaultLanguage !== fields.language) {
        window.setTimeout(() => {
          changeLanguage(fields.language);
          message.success(t('usermodal.info.message.success'), 4);
        }, 0);
      }

      closeUserModal();
    }
  }, [
    t,
    channelLoading,
    channelData,
    channelError,
    defaultLanguage,
    form,
    userPictureFileStore,
    isFormInvalid,
    userData,
    uploadAvatar,
    changeName,
    changeChannelName,
    changeLanguage,
    closeUserModal
  ]);

  // [#30]
  // const themeRadioHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const target = e.target as HTMLInputElement;
  //   const value = target.value as 'light' | 'dark' | 'auto';

  //   uiStore.setTheme(value);
  // };

  return (
    <ModalBlock
      onCancel={closeUserModal}
      visible={isUserModalVisible}
      title={t('usermodal.title')}
      width={920}
      footer={
        <ModalFooterBlock>
          <div
            role="button"
            tabIndex={0}
            className="btn-logout"
            onClick={() => Auth.signOut()}
            key="signout"
          >
            <i className="icon">
              <IconLogout />
            </i>
            <span className="text">{t('common.logout')}</span>
          </div>
          <div className="btn-submit">
            <Button
              type="primary"
              size="large"
              icon={<IconDone />}
              onClick={() => submit()}
              disabled={isFormInvalid}
              loading={onRequesting}
            >
              {t('usermodal.info.submit')}
            </Button>
          </div>
        </ModalFooterBlock>
      }
    >
      <div className="editable-area">
        <div className="avatar-box">
          <Upload
            accept="image/png,image/jpeg"
            showUploadList={false}
            customRequest={(options) => setAvatarPreview(options)}
            className="thumbnail"
          >
            <div className="frame">
              <img
                className="image"
                src={userData?.avatarUrl}
                alt={userData?.name}
                ref={userPictureRef}
                referrerPolicy="no-referrer"
                onError={() => {
                  userPictureRef.current!.src = '/assets/common/userpicture-default-sm.png';
                }}
              />
            </div>
            <div className="hover-dimmer">
              <i className="icon">
                <IconEdit width="24" height="24" />
              </i>
            </div>
          </Upload>
          <span className="avatar-guide">{t('usermodal.info.avatar.guide')}</span>
        </div>
        <div className="edit-box">
          <Form
            ref={form}
            layout="vertical"
            initialValues={{
              user_name: userData?.name,
              channel_name: '',
              language: defaultLanguage,
            }}
            onFieldsChange={onFieldsChange}
            hideRequiredMark
          >
            <Form.Item
              name="user_name"
              label={
                <Tooltip
                  placement="right"
                  title={t('usermodal.info.name.tooltip')}
                  overlayClassName="font-size-12"
                  overlayStyle={{ maxWidth: 'none' }}
                  visible={isFocusedUserName}
                >
                  <div className="form-label">
                    <span className="icon">
                      <IconUsername />
                    </span>
                    {t('usermodal.info.name')}
                  </div>
                </Tooltip>
              }
              className="item-name"
              rules={[
                {
                  required: true,
                  type: 'string',
                  min: 2,
                  max: 30,
                  message: t('usermodal.info.name.validatemsg'),
                },
              ]}
            >
              <Input
                minLength={2}
                maxLength={30}
                autoComplete="off"
                placeholder={t('usermodal.info.name')}
                onFocus={() => setFocusedUserName(true)}
                onBlur={() => setFocusedUserName(false)}
              />
            </Form.Item>
            <Form.Item
              name="channel_name"
              label={
                <Tooltip
                  placement="right"
                  title={t('usermodal.info.channelname.tooltip')}
                  overlayClassName="font-size-12"
                  overlayStyle={{ maxWidth: 'none' }}
                  visible={isFocusedChannelName}
                >
                  <div className="form-label">
                    <span className="icon">
                      <IconSetting />
                    </span>
                    {t('usermodal.info.channelname')}
                  </div>
                </Tooltip>
              }
              className="item-name"
              rules={[
                {
                  required: true,
                  type: 'string',
                  min: 6,
                  max: 20,
                  message: t('usermodal.info.channelname.validatemsg'),
                },
              ]}
            >
              {channelLoading && !channelData ? (
                <LoadingOutlined style={{ margin: '6px 0 12px' }} />
              ) : (
                <Input
                  minLength={6}
                  maxLength={20}
                  autoComplete="off"
                  placeholder={t('usermodal.info.channelname')}
                  onFocus={() => setFocusedChannelName(true)}
                  onBlur={() => setFocusedChannelName(false)}
                />
              )}
            </Form.Item>
            <Form.Item
              name="language"
              label={
                <div className="form-label">
                  <span className="icon">
                    <IconLanguage />
                  </span>
                  {t('usermodal.options.language')}
                </div>
              }
              rules={[
                {
                  required: true,
                  type: 'string',
                },
              ]}
            >
              <Select style={{ width: 222 }}>
                <Option value="en">{t('languages.en')}</Option>
                <Option value="ko">{t('languages.ko')}</Option>
                {/* {
                  i18n.languages.map((value) => (
                    <Option value={value} key={value}>
                      { t(`languages.${value}`) }
                    </Option>
                  ))
                } */}
              </Select>
            </Form.Item>
          </Form>
          {/* [#30] */}
          {/* <div className="form-item type-horizontal">
            <span className="form-label">{t('usermodal.options.theme')}</span>
            <div className="form-content">
              <span className="radio-item">
                <input
                  type="radio"
                  id="theme_light"
                  name="themesGroup"
                  value="light"
                  checked={theme === 'light'}
                  onChange={themeRadioHandler}
                />
                <label htmlFor="theme_light">
                  <i className="icon">
                    <IconThemeLight />
                  </i>
                  <span className="text">Light</span>
                </label>
              </span>
              <span className="radio-item">
                <input
                  type="radio"
                  id="theme_dark"
                  name="themesGroup"
                  value="dark"
                  checked={theme === 'dark'}
                  onChange={themeRadioHandler}
                />
                <label htmlFor="theme_dark">
                  <i className="icon">
                    <IconThemeDark />
                  </i>
                  <span className="text">Dark</span>
                </label>
              </span>
              <span className="radio-item">
                <input
                  type="radio"
                  id="theme_dynamic"
                  name="themesGroup"
                  value="auto"
                  checked={theme === 'auto'}
                  onChange={themeRadioHandler}
                />
                <label htmlFor="theme_dynamic">
                  <i className="icon">
                    <IconThemeDynamic />
                  </i>
                  <span className="text">Dynamic</span>
                </label>
              </span>
            </div>
          </div> */}
        </div>
      </div>
    </ModalBlock>
  );
};

const ModalBlock = styled(Modal)`
  min-width: 920px;

  .ant-modal-header {
    background-color: ${({ theme }) => theme.colors.secondary.bgColor};

    .ant-modal-title {
      font-weight: bold;
      font-size: 18px;
      line-height: 1.5;
      color: ${({ theme }) => theme.colors.fontColor};
    }
  }

  .ant-modal-body {
    padding: 32px 45px;
  }

  .ant-modal-content {
    background-color: ${({ theme }) => theme.colors.secondary.bgColor};
  }

  .ant-form-item-label {
    label {
      color: ${({ theme }) => theme.colors.fontColor};
    }
  }

  .ant-form-item-control-input {
    .ant-input {
      color: ${({ theme }) => theme.colors.fontColor};
    }
  }

  .editable-area {
    display: flex;
    min-height: 148px;
  }

  .avatar-box {
    width: 243px;
    position: relative;
    padding: 166px 28px 0;

    .thumbnail {
      display: block;
      overflow: hidden;
      position: absolute;
      top: 0;
      left: 28px;
      width: 150px;
      height: 150px;
      border-radius: 50%;
      cursor: pointer;
      outline: none;

      &:hover {
        .hover-dimmer {
          opacity: 1;
        }
      }

      .ant-upload.ant-upload-select {
        width: 100%;
        height: 100%;
      }

      img {
        display: block;
        width: 100%;
        height: 100%;
      }
    }

    .hover-dimmer {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      height: 44px;
      background-color: rgba(9, 9, 9, 0.8);
      opacity: 0;
      transition: opacity 200ms ease;

      .icon {
        display: block;
        line-height: 1;

        svg path {
          fill: #fff;
        }
      }
    }

    .avatar-guide {
      display: block;
      width: 150px;
      text-align: center;
      font-size: 13px;
      line-height: 1.35;
      color: #334d6e;
      white-space: pre-wrap;
    }
  }

  .edit-box {
    flex: 1;

    .item-name {
      input {
        max-width: 475px;
        border-width: ${({ theme }) => (theme.name === 'light' ? '1px' : '0')};
        background-color: ${({ theme }) => theme.user.namefield};
      }
    }

    .form-label {
      .icon {
        display: inline-block;
        position: relative;
        top: 1px;
        margin-right: 4px;
        vertical-align: middle;
      }
    }
  }

  .form-item {
    &.type-horizontal {
      .form-label {
        float: left;
      }
    }

    .form-label {
      display: inline-block;
      min-width: 160px;
      color: ${({ theme }) => theme.colors.fontColor};
    }

    .form-content {
      .radio-item {
        & + .radio-item {
          margin-left: 68px;
        }
      }

      .icon {
        vertical-align: top;
      }

      .text {
        display: inline-block;
        margin-left: 8px;
        vertical-align: top;
        color: ${({ theme }) => theme.colors.fontColor};
      }

      label {
        font-size: 14px;
        line-height: 1.5;
      }

      input[type='radio'] {
        ${scMixinScreenReaderOnly};
      }

      input[type='radio']:checked + label,
      input[type='radio']:focus + label {
        .icon svg path {
          fill: ${({ theme }) => theme.colors.primary.btnColor};
        }

        .text {
          color: ${({ theme }) => theme.colors.primary.btnColor};
        }
      }
    }
  }
`;

const ModalFooterBlock = styled.div`
  padding: 8px 34px 8px 56px;

  .btn-logout {
    float: left;
    margin-top: 8px;
    cursor: pointer;

    &:focus {
      outline: none;
    }

    .icon {
      display: inline-block;
      height: 24px;
      vertical-align: top;
    }

    .text {
      display: inline-block;
      margin-left: 10px;
      font-size: 15px;
      vertical-align: top;
      line-height: 24px;
      color: ${({ theme }) => theme.colors.headingColor};
    }
  }

  .btn-submit {
    display: inline-block;
    clear: both;

    svg {
      display: inline-block;
      position: relative;
      top: 1px;
      margin-right: 12px;
    }

    button:disabled svg {
      transition: all 200ms ease;

      path {
        fill: #c2cfe0;
      }
    }
  }
`;

export default UserModal;
